import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import  DealerService  from '../../service/DealerService';
import { SKU_PACKAGE_STATUS } from '../../config/constant';
import { toast } from 'react-toastify';

const EditCreateSkuForm = ({ 
    modelData = {},
    modelAction,
    onSubmitted, 
    skuTypes,
    listingTypes,
    handleClose,
    isLoading,
    roleAccess,
    fetchSkuList
}) => {
    const history = useHistory();
    const [id, setId] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({ gracePeriod: 0, discount: 0 });
    const [dealerOptions, setDealerOptions] = useState([]);
    const [dealerLoading, setDealerLoading] = useState(false);
    const [skuTYpes, setSkuTypes] = useState(skuTypes);
    const [skuSubsciptionTypes, setSkuSubscriptionTypes] = useState([]);
    const [skuListingTypes, setSkuListingTypes] = useState(listingTypes);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        fetchDealers();
        if (modelAction === 'update') {
            setIsEdit(true);
            setId(modelData?.id);
        } else {
            setIsEdit(false);
            setId('');
        }
        // if (!isEdit) fetchDealers();
    }, []);

    useEffect(() => {
        if (id && skuTYpes?.length > 0) {
            fetchSkuDataById(id);
        }
    }, [id, skuTYpes]);

    const fetchDealers = async (inputValue = '', limit = 50) => {
        try {
            setDealerLoading(true);
            const payload = {
                search_by: 'organization',
                search_text: inputValue,
                limit: limit
            };
            const response = await DealerService.getDealerDetailsByIds(payload);

            if (response?.data?.status === 200) {
                const dealers = response.data.data || [];
                const options = dealers.map(dealer => ({
                    value: dealer.id,
                    label: `${dealer.organization} (GCD${dealer.id})`
                }));
                setDealerOptions(options);
                return options;
            }
        } catch (error) {
            console.error("Failed to fetch dealers", error);
            return [];
        } finally {
            setDealerLoading(false);
        }
    };

    const fetchSkuDataById = async (skuId) => {
        try {
            setDealerLoading(true);
            const response = await DealerService.getSkuPackageById(skuId);

            if (response?.data?.status === 200) {
                const record = response.data.data;

                let selectedDealers = [];
                const existSKYType = skuTYpes.find((t) => t.value === record.sku_type);
                const dealerIds = record?.dealer_ids || [];

                if (dealerIds.length > 0) {
                    const dealerResp = await DealerService.getDealerDetailsByIds({ search_by: "gcd_code", search_text: dealerIds });

                    if (dealerResp?.data?.status === 200) {
                        selectedDealers = (dealerResp.data.data || []).filter((v) => dealerIds.includes(v.id)).map(dealer => ({
                            value: dealer.id,
                            label: dealer.organization
                        }));
                    }
                }

                // Fill form fields based on API response
                setFormData({
                    name: record.name || '',
                    quota: record.quota || '',
                    totalLeads: record.total_leads || '',
                    dailyLeads: record.daily_leads || '',
                    activePeriod: record.active_period_days || '',
                    gracePeriod: record.grace_period_days || 0,
                    skuType: existSKYType,
                    listingType: skuListingTypes.find(opt => opt.value === record.listing_type),
                    price: record.price || '',
                    discount: record?.discount_to_be_shown || 0,
                    vat: record.vat || 0,
                    status: SKU_PACKAGE_STATUS.find(opt => opt.value === record.is_enable?.toString()),
                    dealers: selectedDealers, //dealerOptions.filter(opt => record.dealer_ids?.includes(opt.value)),
                    showToDealers: record.is_shown_dealers === '1',
                });
            } else {
                alert("Failed to load SKU record");
            }
        } catch (error) {
            console.log("API error:", error);
            toast.error('Failed to load SKU data');
        } finally {
            setDealerLoading(false);
        }
    };

    const handleChange = (e) => {
        let { name, value, type, checked } = e.target;

        if (name === 'gracePeriod') {
            if (+value < 0 || !value || isNaN(+value)) {
                value = 0;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (field, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [field]: selectedOption
        }));
    };

    const handleMultiSelectChange = (selected) => {
        setFormData(prev => ({
            ...prev,
            dealers: selected
        }));
    };

    const formValidation = () => {
        const errors = {};

        if (!formData.name || formData.name.trim() === '') {
            errors.name = 'Name is required.';
        }

        if (!formData.quota || isNaN(formData.quota) || formData.quota <= 0) {
            errors.quota = 'Quota must be a number greater than 0.';
        }

        if (!formData.totalLeads || isNaN(formData.totalLeads) || formData.totalLeads <= 0) {
            errors.totalLeads = 'Total leads must be a number greater than 0.';
        }

        if (!formData.dailyLeads || isNaN(formData.dailyLeads) || formData.dailyLeads <= 0) {
            errors.dailyLeads = 'Daily leads must be a number greater than 0.';
        }

        if (!formData.activePeriod || isNaN(formData.activePeriod) || formData.activePeriod <= 0) {
            errors.activePeriod = 'Active period must be a number greater than 0.';
        }

        if (
            formData.gracePeriod === '' ||
            isNaN(formData.gracePeriod) ||
            Number(formData.gracePeriod) < 0
        ) {
            errors.gracePeriod = 'Grace period must be 0 or more.';
        }


        if (!formData.skuType || !formData.skuType.value) {
            errors.skuType = 'SKU Type is required.';
        }

        if (!formData.listingType || !formData.listingType.value) {
            errors.listingType = 'Listing Type is required.';
        }

        if (!formData.price || isNaN(formData.price) || formData.price <= 0) {
            errors.price = 'Price must be a number greater than 0.';
        }

        if (formData.discount && (isNaN(formData.discount) || formData.discount < 0)) {
            errors.discount = 'Discount must be 0 or more.';
        }

        if (!formData.vat || isNaN(formData.vat) || formData.vat < 0) {
            errors.vat = 'VAT must be a number 0 or more.';
        }

        // if (!Array.isArray(formData.dealers) || formData.dealers.length === 0) {
        //     errors.dealers = 'At least one dealer must be selected.';
        // }

        if (!formData.status || !formData.status.value) {
            errors.status = 'Status is required.';
        }

        return errors;
    };


    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!formData.gracePeriod) formData.gracePeriod = 0;

        const errors = formValidation();

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            toast.error('Required Fields are missing');
            return;
        }

        setFormErrors({});

        const payload = {
            name: formData.name,
            quota: +formData.quota,
            total_leads: +formData.totalLeads,
            daily_leads: +formData.dailyLeads,
            active_period_days: +formData.activePeriod,
            grace_period_days: +formData.gracePeriod || 0,
            sku_type: formData.skuType?.value,
            listing_type: formData.listingType?.value,
            price: +formData.price,
            discount_to_be_shown: +formData.discount || 0,
            vat: +formData.vat,
            is_enable: formData.status?.value,
            is_shown_dealers: formData.showToDealers === true ? '1' : '0',
            dealer_ids: []
        };

        if (formData.dealers && formData.dealers?.length > 0) {
            payload['dealer_ids'] = formData.dealers.map(d => d.value);
        }

        if (id) {
            payload['id'] = id;
        }

        try {
            setDealerLoading(true);
            const response = isEdit ? await DealerService.updateSkuPackage(payload) : await DealerService.saveSkuPackage(payload);

            if (response && response.status === 200) {
                if (response?.data && response?.data?.status === 200) {
                    handleClose(true);
                    // history.push('/admin/dealer_sku');
                }
            }
            // alert(`SKU ${isEdit ? 'updated' : 'created'} successfully!`);
            // if (onSubmitted) onSubmitted(response.data);
        } catch (error) {
            console.error('API error:', error);
            toast.error('Failed to save SKU package');
        } finally {
            setDealerLoading(false);
        }
    };

    return (
        <form onSubmit={handleFormSubmit} className= {dealerLoading ? 'p-lg loading' : 'p-lg'}>
            <div className="form-grid">
                <div className="form-group m-0-b">
                    <label>Name</label>
                    <input name="name" value={formData.name} onChange={handleChange} placeholder='Enter Name'/>
                    {formErrors.name && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.name}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Quota</label>
                    <input name="quota" type="number" value={formData.quota} onChange={handleChange} placeholder='Enter Quota'/>
                    {formErrors.quota && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.quota}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Status</label>
                    <Select options={SKU_PACKAGE_STATUS} value={formData.status} onChange={(opt) => handleSelectChange('status', opt)} />
                    {formErrors.status && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.status}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Total Leads</label>
                    <input name="totalLeads" type="number" value={formData.totalLeads} onChange={handleChange} placeholder='Enter Leads Count' />
                    {formErrors.totalLeads && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.totalLeads}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Daily Leads</label>
                    <input name="dailyLeads" type="number" value={formData.dailyLeads} onChange={handleChange} placeholder='Enter Daily Leads Count'/>
                    {formErrors.dailyLeads && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.dailyLeads}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Active Period (days)</label>
                    <input name="activePeriod" type="number" value={formData.activePeriod} onChange={handleChange} placeholder='Enter Days'/>
                    {formErrors.activePeriod && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.activePeriod}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>SKU Type</label>
                    <Select options={skuTYpes} value={formData.skuType} onChange={(opt) => handleSelectChange('skuType', opt)} />
                    {formErrors.skuType && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.skuType}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Listing Type</label>
                    <Select options={skuListingTypes} value={formData.listingType} onChange={(opt) => handleSelectChange('listingType', opt)} />
                    {formErrors.listingType && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.listingType}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Grace Period (days)</label>
                    <input name="gracePeriod" type="number" value={formData.gracePeriod} onChange={handleChange} placeholder='Enter Days'/>
                    {formErrors.gracePeriod && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.gracePeriod}
                        </div>
                    )}
                </div>
                <div className="form-group m-0-b">
                    <label>VAT</label>
                    <input name="vat" type="number" value={formData.vat} onChange={handleChange} placeholder='Enter Vat'/>
                    {formErrors.vat && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.vat}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Price</label>
                    <input name="price" type="number" value={formData.price} onChange={handleChange} placeholder='Enter Price'/>
                    {formErrors.price && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.price}
                        </div>
                    )}
                </div>

                <div className="form-group m-0-b">
                    <label>Discount to be shown</label>
                    <input name="discount" type="number" value={formData.discount} onChange={handleChange} placeholder='Enter Discount'/>
                    {formErrors.discount && (
                        <div
                            style={{
                                color: 'red',
                                fontSize: '12px',
                                marginTop: '4px',
                                display: 'block',
                                position: 'static', // Ensure it doesn't float above
                            }}
                        >
                            {formErrors.discount}
                        </div>
                    )}
                </div>

                {/* <div className="form-group m-0-b">
                    <label>Dealers</label>
                    <input type='text'
                        placeholder='Enter Dealers '
                        value={formData.dealers}
                        onChange={handleMultiSelectChange}
                    />
                </div> */}

                <div className="form-group  m-0-b">
                    <label>Dealers</label>
                    <AsyncSelect
                        isMulti
                        cacheOptions
                        defaultOptions={dealerOptions}
                        loadOptions={fetchDealers}
                        value={formData.dealers}
                        onChange={handleMultiSelectChange}
                        isLoading={dealerLoading}
                        placeholder="Search dealer by organization name..."
                    />
                </div>

                <div className="form-group m-0-b">
                    <label>
                        <input
                            type="checkbox"
                            name="showToDealers"
                            checked={formData.showToDealers}
                            onChange={handleChange}
                        />{' '}
                        Show to dealers
                    </label>
                </div>
            </div>

            <div className="button-container m-md-b m-sm-t">
                <button type="submit" className="btn-solid w-auto bg-orange t-uppercase p-lg-l p-lg-r">
                    {isEdit ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-default m-lg-l" onClick={() => handleClose() }>Cancel</button>
            </div>
        </form>
    );
};

export default EditCreateSkuForm;
